
import type { Dispatch } from 'redux';

import { CLOSE_DIALOGS, CLOSE_NAVIGATION_BAR_OVERFLOW_MENU, HIDE_DIALOG, HIDE_SHEET, OPEN_DIALOG, OPEN_NAVIGATION_BAR_OVERFLOW_MENU, OPEN_SHEET } from './actionTypes';
import { isDialogOpen } from './functions';

/**
 * Signals Dialog to close its dialog.
 *
 * @param {Object} [component] - The {@code Dialog} component to close/hide. If
 * {@code undefined}, closes/hides {@code Dialog} regardless of which
 * component it's rendering; otherwise, closes/hides {@code Dialog} only if
 * it's rendering the specified {@code component}.
 * @returns {{
 *     type: HIDE_DIALOG,
 *     component: (React.Component | undefined)
 * }}
 */
export function hideDialog(component: ?Object) {
    return {
        type: HIDE_DIALOG,
        component
    };
}

/**
 * Closes the active sheet.
 *
 * @returns {{
*     type: HIDE_SHEET,
* }}
*/
export function hideSheet() {
    return {
        type: HIDE_SHEET
    };
}

/**
 * Signals Dialog to close its dialogs.
 *
 * @param {boolean} [shouldClose] - The {@code Dialog} component to close/hide. If
 * {@code undefined}, closes/hides {@code Dialog} regardless of which
 * component it's rendering; otherwise, closes/hides {@code Dialog} only if
 * it's rendering the specified {@code component}.
 * @returns {{
 *     type: CLOSE_DIALOGS,
 *     component: (React.Component | undefined)
 * }}
 */
export function closeDialogs(shouldClose: ?boolean) {
    return {
        type: CLOSE_DIALOGS,
        shouldClose
    };
}

/**
 * Signals Dialog to open dialog.
 *
 * @param {Object} component - The component to display as dialog.
 * @param {Object} [componentProps] - The React {@code Component} props of the
 * specified {@code component}.
 * @param {boolean} rawDialog - True if the dialog is a raw dialog.
 * (Doesn't inherit behavior from other common frameworks).
 * @returns {{
 *     type: OPEN_DIALOG,
 *     component: React.Component,
 *     componentProps: (Object | undefined)
 * }}
 */
export function openDialog(component: Object, componentProps: ?Object, rawDialog?: boolean) {
    return {
        type: OPEN_DIALOG,
        component,
        componentProps,
        rawDialog
    };
}

/**
 * Signals Dialog to open a dialog with the specified component if the component
 * is not already open. If it is open, then Dialog is signaled to close its
 * dialog.
 *
 * @param {Object} component - The component to display as dialog.
 * @param {Object} [componentProps] - The React {@code Component} props of the
 * specified {@code component}.
 * @returns {Function}
 */
export function toggleDialog(component: Object, componentProps: ?Object) {

    return (dispatch: Dispatch<any>, getState: Function) => {
        if (isDialogOpen(getState, component)) {
            dispatch(hideDialog(component));
        } else {
            dispatch(openDialog(component, componentProps));
        }
    };
}

/**
 * Opens the requested sheet.
 *
 * @param {Object} component - The component to display as a sheet.
 * @param {Object} [componentProps] - The React {@code Component} props of the
 * specified {@code component}.
 * @returns {{
*     type: OPEN_SHEET,
*     component: React.Component,
*     componentProps: (Object | undefined)
* }}
*/
export function openSheet(component, componentProps) {
    return {
        type: OPEN_SHEET,
        component,
        componentProps
    };
}

/**
 * Opens Requested Menu
 *
 * @param {Boolean} isNavigationBarOverflowMenuOpen - The value recieves user's action when three dots menu button is clicked.
 * @returns {{
*     type: OPEN_NAVIGATION_BAR_OVERFLOW_MENU,
*     isNavigationBarOverflowMenuOpen: boolean
* }}
*/
export function openNavigationBarOverflowMenu(isNavigationBarOverflowMenuOpen) {
    // logger.log(isNavigationBarOverflowMenuOpen, 'actionsIs===');

    return {
        type: OPEN_NAVIGATION_BAR_OVERFLOW_MENU,
        isNavigationBarOverflowMenuOpen
    };
}

/**
 * Closes Requested Menu
 *
 * @param {Boolean} isNavigationBarOverflowMenuOpen - The value recieves user's action when three dots menu button is clicked.
 * @returns {{
*     type: CLOSE_NAVIGATION_BAR_OVERFLOW_MENU,
*     isNavigationBarOverflowMenuOpen: boolean
* }}
*/
export function closeNavigationBarOverflowMenu(isNavigationBarOverflowMenuOpen) {
    return {
        type: CLOSE_NAVIGATION_BAR_OVERFLOW_MENU,
        isNavigationBarOverflowMenuOpen
    };
}
